import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CanvasView } from "@/components/thumbnail/CanvasView";
import {
  Search, Plus, Youtube, Layout, Instagram, Monitor, Smartphone,
  Image as ImageIcon, Eye, TrendingUp, Sparkles, Wand2, Loader2,
} from "lucide-react";

const ICON_MAP = {
  youtube: Youtube, layout: Layout, instagram: Instagram,
  monitor: Monitor, smartphone: Smartphone, image: ImageIcon,
};

const fmtViews = (n) => {
  if (!n) return null;
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return n.toString();
};

export default function ThumbnailStudio() {
  const nav = useNavigate();
  const [presets, setPresets] = useState([]);
  const [niches, setNiches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [q, setQ] = useState("");
  const [niche, setNiche] = useState("All");
  const [busy, setBusy] = useState(false);

  const loadAll = async () => {
    try {
      const [p, pop, mine] = await Promise.all([
        api.get("/thumbnail-studio/presets"),
        api.get("/thumbnail-studio/templates/popular?limit=8"),
        api.get("/thumbnail-studio/my-designs?limit=3"),
      ]);
      setPresets(p.data.presets); setNiches(p.data.niches);
      setPopular(pop.data.templates);
      setRecent(mine.data.designs);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const loadTemplates = async () => {
    const params = new URLSearchParams();
    if (niche && niche !== "All") params.set("niche", niche);
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "60");
    const { data } = await api.get(`/thumbnail-studio/templates?${params}`);
    setTemplates(data.templates);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadTemplates(); }, [niche]);
  useEffect(() => {
    const t = setTimeout(loadTemplates, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const createBlank = async (format_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/thumbnail-studio/designs", { format_id, title: "Untitled design" });
      nav(`/app/studio/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const applyTemplate = async (tpl) => {
    setBusy(true);
    try {
      const { data } = await api.post("/thumbnail-studio/designs", { template_id: tpl.id, title: tpl.title });
      nav(`/app/studio/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const [ocTopic, setOcTopic] = useState("");
  const [ocStyle, setOcStyle] = useState("bold cinematic");
  const [ocBusy, setOcBusy] = useState(false);
  const [ocVariants, setOcVariants] = useState(null);  // {variants:[...], topic, style, has_image}
  const [ocPicking, setOcPicking] = useState(false);   // saving chosen variant

  const runOneClick = async () => {
    if (ocTopic.trim().length < 3) { toast.error("Enter a topic (min 3 chars)"); return; }
    setOcBusy(true);
    try {
      toast.message("Generating 3 layout variants + AI background… (~30-45s)");
      const { data } = await api.post("/thumbnail-studio/ai/one-click-variants", {
        topic: ocTopic.trim(), style: ocStyle, language: "English", format_id: "yt-thumb",
      });
      setOcVariants(data);
    } catch (e) { toast.error(e.response?.data?.detail || "One-click AI failed. Please retry."); }
    finally { setOcBusy(false); }
  };

  const pickVariant = async (variant) => {
    setOcPicking(true);
    try {
      const { data } = await api.post("/thumbnail-studio/designs", {
        title: variant.title, format_id: "yt-thumb", canvas: variant.canvas,
      });
      nav(`/app/studio/${data.id}/edit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setOcPicking(false); }
  };

  return (
    <div className="space-y-10 vp-fade-up">
      {/* One-Click AI hero */}
      <section
        className="rounded-2xl p-6 sm:p-8 border border-[#7C3AED]/30 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#1E1B4B 0%,#3B0764 45%,#4C1D95 100%)" }}
        data-testid="one-click-hero"
      >
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[#F0ABFC]/20 blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-52 h-52 rounded-full bg-[#22D3EE]/15 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[11px] uppercase tracking-widest text-[#F0ABFC] font-semibold mb-3 border border-[#F0ABFC]/30">
            <Sparkles className="w-3.5 h-3.5" /> One-Click AI
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight">
            Type a topic. Pick your favourite of 3 AI variants.
          </h2>
          <p className="text-[#CBD5E1] mt-2 max-w-2xl text-sm">
            Kimi writes 3 punchy titles, Nano Banana renders a scene-perfect background, and Videoepreneur composes 3 different layouts. Every layer stays editable.
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-[1fr_180px_auto] gap-2 items-stretch">
            <input
              className="rounded-lg px-4 py-3 bg-white/10 border border-white/25 text-white placeholder:text-white/50 text-sm focus:outline-none focus:border-[#F0ABFC]"
              placeholder="Topic (e.g. Why 90% traders lose money)"
              value={ocTopic}
              onChange={(e) => setOcTopic(e.target.value)}
              data-testid="one-click-hero-topic"
            />
            <select
              className="rounded-lg px-3 py-3 bg-white/10 border border-white/25 text-white text-sm focus:outline-none focus:border-[#F0ABFC]"
              value={ocStyle}
              onChange={(e) => setOcStyle(e.target.value)}
              data-testid="one-click-hero-style"
            >
              <option value="bold cinematic">Bold cinematic</option>
              <option value="minimal editorial">Minimal editorial</option>
              <option value="neon tech">Neon tech</option>
              <option value="pastel finance">Pastel finance</option>
            </select>
            <button
              onClick={runOneClick}
              disabled={ocBusy}
              className="rounded-lg px-6 py-3 bg-gradient-to-r from-[#F0ABFC] to-[#22D3EE] text-[#1E1B4B] font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-70 hover:scale-[1.02] transition-transform"
              data-testid="one-click-hero-btn"
            >
              {ocBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {ocBusy ? "Cooking…" : "Generate 3 variants"}
            </button>
          </div>
          <div className="mt-3 text-[11px] text-white/50">
            Kimi title · Nano Banana background · 3 variants to pick from · Editable layers
          </div>
        </div>
      </section>

      {/* Variants picker — shows after successful generation */}
      {ocVariants && (
        <section className="vp-card p-6" data-testid="one-click-variants-panel">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="vp-label" style={{ color: "#7C3AED" }}>Pick your favourite</div>
              <div className="font-display font-black text-xl mt-1">3 AI variants for "{ocVariants.topic}"</div>
              <div className="text-xs text-[#747680] mt-1">
                {ocVariants.has_image ? "Nano Banana background · Kimi titles" : "Kimi titles · Solid color background (image gen skipped/failed)"}
              </div>
            </div>
            <button
              onClick={() => setOcVariants(null)}
              className="text-xs text-[#4A4D57] hover:text-[#0B0D17] underline"
              data-testid="one-click-variants-close"
            >
              ✕ Close variants
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ocVariants.variants.map((v) => (
              <button
                key={v.id}
                onClick={() => pickVariant(v)}
                disabled={ocPicking}
                data-testid={`variant-${v.layout}`}
                className="group relative rounded-xl overflow-hidden border-2 border-[#E4E6EC] hover:border-[#7C3AED] transition-all disabled:opacity-50 text-left"
              >
                <div className="aspect-video bg-[#F1F3F7] pointer-events-none">
                  <CanvasView
                    canvas={v.canvas}
                    format={{ w: 1280, h: 720, ratio: "16/9" }}
                    editable={false}
                  />
                </div>
                <div className="p-3 bg-white">
                  <div className="text-xs text-[#747680] uppercase tracking-widest">{v.layout.replace(/-/g, " ")}</div>
                  <div className="font-display font-bold mt-1 line-clamp-2">{v.title}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#7C3AED] font-semibold group-hover:underline">
                    {ocPicking ? "Saving…" : "Use this variant →"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Format picker */}
      <section
        className="vp-glass p-6 sm:p-8 rounded-2xl"
        style={{ background: "linear-gradient(135deg,#FCE7F3 0%,#FEF3C7 50%,#D1FAE5 100%)" }}
      >
        <div className="text-center mb-6">
          <h1 className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tight">Thumbnail Studio</h1>
          <p className="text-[#4A4D57] mt-2 text-sm inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#0B5CFF]" />
            Steal proven layouts from India's Top 50 YouTubers · 750+ real thumbnails across 15 niches
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {presets.map((p) => {
            const Icon = ICON_MAP[p.icon] || ImageIcon;
            return (
              <button
                key={p.id} disabled={busy} onClick={() => createBlank(p.id)}
                data-testid={`format-${p.id}`}
                className="vp-card p-4 text-center hover:border-[#0B5CFF]/50 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2" style={{ background: p.accent + "18" }}>
                  <Icon className="w-5 h-5" strokeWidth={1.75} style={{ color: p.accent }} />
                </div>
                <div className="font-display font-semibold text-xs">{p.label}</div>
                <div className="text-[10px] text-[#747680] mt-0.5">{p.w} × {p.h}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Popular */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-black text-xl sm:text-2xl inline-flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#DC2626]" />
            Top-performing thumbnails
          </div>
          <button onClick={() => setNiche("All")} className="text-sm text-[#0B5CFF] hover:underline">Browse all →</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {popular.map((t) => (
            <TemplateCard key={t.id} t={t} onUse={applyTemplate} />
          ))}
        </div>
      </section>

      {/* Recent */}
      {recent.length > 0 && (
        <section>
          <div className="font-display font-black text-xl sm:text-2xl mb-4">Your recent designs</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {recent.map((d) => (
              <div key={d.id} className="vp-card overflow-hidden group">
                <div
                  onClick={() => nav(`/app/studio/${d.id}/edit`)}
                  className="cursor-pointer bg-[#F1F3F7]"
                  style={{ aspectRatio: "16/9" }}
                >
                  {d.preview_url
                    ? <img src={d.preview_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full flex items-center justify-center text-[#747680] text-xs">No preview yet</div>
                  }
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{d.title}</div>
                    <div className="text-xs text-[#747680]">Edited {new Date(d.edited_at).toLocaleDateString()}</div>
                  </div>
                  <button
                    onClick={() => nav(`/app/studio/${d.id}/edit`)}
                    data-testid={`open-design-${d.id}`}
                    className="text-xs text-[#0B5CFF] hover:underline font-semibold">Open →</button>
                </div>
              </div>
            ))}
            <button
              onClick={() => createBlank("yt-thumb")}
              data-testid="new-blank-design"
              className="vp-card border-dashed flex flex-col items-center justify-center py-10 hover:border-[#0B5CFF] transition-all"
            >
              <Plus className="w-8 h-8 text-[#4A4D57]" strokeWidth={1.5} />
              <div className="font-medium mt-2 text-sm">New blank design</div>
            </button>
          </div>
        </section>
      )}

      {/* All templates */}
      <section>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-baseline gap-3">
            <div className="font-display font-black text-xl sm:text-2xl">All templates</div>
            <div className="text-sm text-[#747680]">{templates.length} shown</div>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-[#747680] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              className="vp-input pl-10"
              placeholder="Search titles…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="template-search"
            />
          </div>
        </div>
        {/* Niche chips */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {["All", ...niches].map((n) => {
            const active = niche === n;
            return (
              <button
                key={n} onClick={() => setNiche(n)}
                data-testid={`niche-${n}`}
                className={"px-4 py-1.5 rounded-full text-sm font-medium transition-all " + (active ? "bg-[#0B5CFF] text-white" : "bg-[#F1F3F7] text-[#4A4D57] hover:bg-[#E4E6EC]")}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {templates.map((t) => (
            <TemplateCard key={t.id} t={t} onUse={applyTemplate} />
          ))}
          {templates.length === 0 && (
            <div className="col-span-full text-center py-10 text-[#4A4D57] text-sm">
              No templates match — try a different niche or search term.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TemplateCard({ t, onUse }) {
  const isShort = t.format_id === "yt-short" || t.format_id === "ig-story";
  const src = t.thumbnail_url || t.canvas?.bg?.url;
  return (
    <div
      className="relative group cursor-pointer"
      onClick={() => onUse(t)}
      data-testid={`template-card-${t.id}`}
    >
      <div className="rounded-xl overflow-hidden vp-card p-0 bg-black">
        <div style={{ aspectRatio: isShort ? "9/16" : "16/9" }}>
          <img
            src={src}
            alt={t.title}
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => {
              if (e.currentTarget.src.includes("maxresdefault")) {
                e.currentTarget.src = e.currentTarget.src.replace("maxresdefault", "hqdefault");
              }
            }}
          />
        </div>
      </div>
      <div className="absolute top-2 left-2 vp-chip bg-black/70 text-white text-[10px]">{t.niche}</div>
      {t.views ? (
        <div className="absolute top-2 right-2 vp-chip bg-black/70 text-white text-[10px] inline-flex items-center gap-1">
          <Eye className="w-3 h-3" /> {fmtViews(t.views)}
        </div>
      ) : null}
      <button
        onClick={(e) => { e.stopPropagation(); onUse(t); }}
        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-xl"
      >
        <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-[#0B0D17] font-semibold text-sm shadow-lg">
          Use template →
        </span>
      </button>
      <div className="mt-2 min-h-[2.75rem]">
        <div className="text-sm font-medium line-clamp-2 leading-tight">{t.title}</div>
        {t.channel && <div className="text-[11px] text-[#747680] mt-0.5 truncate">{t.channel}</div>}
      </div>
    </div>
  );
}
